import { reportException, reportExceptionBeforeNavigation } from '@/services/sentry.ts';

const CHUNK_LOAD_RELOAD_KEY = 'velos:chunk-load-reload-url';
const WINDOW_NAME_RELOAD_MARKER_PREFIX = 'velos:chunk-load-reload-url:';

const CHUNK_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
  'loading chunk',
  'chunkloaderror',
  'unable to preload css for ',
];

const isJavaScriptModuleMimeError = (errorText: string) => {
  if (!errorText.includes('text/html')) return false;

  return (
    errorText.includes('javascript mime type') ||
    (errorText.includes('module script') && errorText.includes('mime type'))
  );
};

type VitePreloadErrorEvent = Event & {
  detail?: unknown;
  payload?: unknown;
};

const getErrorText = (error: unknown) => {
  try {
    if (error instanceof Error) return `${error.name} ${error.message}`.toLowerCase();
    if (typeof error === 'string') return error.toLowerCase();
    return (JSON.stringify(error) ?? '').toLowerCase();
  } catch {
    return '';
  }
};

export const isChunkLoadError = (error: unknown) => {
  const errorText = getErrorText(error);
  return (
    CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => errorText.includes(pattern)) ||
    isJavaScriptModuleMimeError(errorText)
  );
};

const getWindowNameReloadUrl = () => {
  const marker = window.name
    .split('\n')
    .find((entry) => entry.startsWith(WINDOW_NAME_RELOAD_MARKER_PREFIX));

  if (!marker) return null;

  try {
    return decodeURIComponent(marker.slice(WINDOW_NAME_RELOAD_MARKER_PREFIX.length));
  } catch {
    return null;
  }
};

const setWindowNameReloadUrl = (url: string) => {
  try {
    const existingEntries = window.name
      .split('\n')
      .filter((entry) => !entry.startsWith(WINDOW_NAME_RELOAD_MARKER_PREFIX));

    window.name = [
      ...existingEntries,
      `${WINDOW_NAME_RELOAD_MARKER_PREFIX}${encodeURIComponent(url)}`,
    ]
      .filter(Boolean)
      .join('\n');

    return true;
  } catch {
    return false;
  }
};

export const shouldReloadCurrentUrl = (currentUrl: string) => {
  try {
    if (window.sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === currentUrl) {
      return false;
    }

    window.sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, currentUrl);
    return true;
  } catch {
    try {
      if (getWindowNameReloadUrl() === currentUrl) return false;
      return setWindowNameReloadUrl(currentUrl);
    } catch {
      return false;
    }
  }
};

type ChunkFailure = {
  contexts: Record<string, Record<string, unknown>>;
  onReactHandled?: () => void;
};

const failures = new WeakMap<object, ChunkFailure>();
// Strings have no object identity. Correlate matching in-flight observations
// from Vite and React, without retaining them after reporting has settled.
const pendingStringFailures = new Map<string, { url: string; failure: ChunkFailure }>();
const MAX_PENDING_STRING_FAILURES = 100;
const RECOVERY_REPORT_BUDGET_MS = 1_000;
// Reserve at least half the total budget for SDK initialization and delivery
// when an import has no React boundary (or React has not committed yet).
const REACT_CONTEXT_WAIT_MS = 500;
let reloadScheduled = false;

// Both Vite and React see the same rejected import. They share ownership of
// that occurrence, retaining React context without reporting a synthetic error.
// true means this failure is owned here, including when recovery is exhausted.
export const handleChunkLoadError = (error: unknown, componentStack?: string | null) => {
  if (!isChunkLoadError(error) || typeof window === 'undefined') {
    return false;
  }

  const currentUrl = window.location.href;
  const objectError =
    error !== null && (typeof error === 'object' || typeof error === 'function') ? error : null;
  const pendingString = typeof error === 'string' ? pendingStringFailures.get(error) : undefined;
  const existing = objectError
    ? failures.get(objectError)
    : componentStack && pendingString?.url === currentUrl
      ? pendingString.failure
      : undefined;
  if (existing) {
    if (componentStack) {
      existing.contexts.react = { componentStack };
      existing.onReactHandled?.();
    }
    return true;
  }

  const shouldReload =
    import.meta.env.PROD && !reloadScheduled && shouldReloadCurrentUrl(currentUrl);
  if (shouldReload) reloadScheduled = true;
  const deadline = Date.now() + RECOVERY_REPORT_BUDGET_MS;

  const failure: ChunkFailure = {
    contexts: {
      chunkRecovery: { reloadScheduled: shouldReload },
      ...(componentStack ? { react: { componentStack } } : {}),
    },
  };
  if (objectError) failures.set(objectError, failure);
  if (typeof error === 'string') {
    if (pendingStringFailures.size >= MAX_PENDING_STRING_FAILURES) {
      pendingStringFailures.delete(pendingStringFailures.keys().next().value!);
    }
    pendingStringFailures.set(error, { url: currentUrl, failure });
  }

  // React can commit the boundary in a later task. Use its explicit signal
  // rather than assuming a microtask or timer runs after componentDidCatch.
  // Imports outside React still report after a bounded context wait.
  let contextTimer: ReturnType<typeof setTimeout> | undefined;
  const reactHandled = componentStack
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        failure.onReactHandled = resolve;
        contextTimer = setTimeout(resolve, REACT_CONTEXT_WAIT_MS);
      });
  void reactHandled
    .then(() => {
      clearTimeout(contextTimer);
      failure.onReactHandled = undefined;
      return reportExceptionBeforeNavigation(
        error,
        { contexts: failure.contexts },
        Math.max(1, deadline - Date.now()),
      );
    })
    .catch(() => {
      // A reporting failure must not strand recovery or replace the import error.
    })
    .finally(() => {
      if (typeof error === 'string' && pendingStringFailures.get(error)?.failure === failure) {
        pendingStringFailures.delete(error);
      }
      if (!shouldReload) return;
      reloadScheduled = false;
      if (window.location.href === currentUrl) {
        try {
          window.location.reload();
        } catch (reloadError) {
          // Keep the original failure and manual fallback; report the failed
          // recovery separately rather than rejecting this detached promise.
          reportException(reloadError, { contexts: { chunkRecovery: { phase: 'reload' } } });
        }
      }
    });
  return true;
};

let unregister: (() => void) | undefined;

export const registerChunkLoadRecovery = () => {
  if (typeof window === 'undefined') return () => {};
  if (unregister) return unregister;

  const listener = (event: Event) => {
    const preloadErrorEvent = event as VitePreloadErrorEvent;
    const error = preloadErrorEvent.payload ?? preloadErrorEvent.detail ?? event;
    handleChunkLoadError(error);
  };
  window.addEventListener('vite:preloadError', listener);
  const cleanup = () => {
    window.removeEventListener('vite:preloadError', listener);
    if (unregister === cleanup) unregister = undefined;
  };
  unregister = cleanup;
  return unregister;
};
